import { Add as AddIcon, DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import { Alert, Box, Button, IconButton, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import ErrorAlert from '../components/Common/ErrorAlert';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { ParameterSelect, ToneMark, ToneSelect } from '../components/Statements/StatementControls';
import { useDirection } from '../context/DirectionContext';
import { translate as tr } from '../i18n/translations';
import apiService from '../services/api';
import { StatementTemplate, StatementTone } from '../types/api';
import { getErrorMessage } from '../utils/errorUtils';

/**
 * The unit's statement bank (P2) — like the station and form banks. Cycles
 * choose statements from here on their Statements tab and keep a snapshot,
 * so editing or deleting a statement here never changes an existing cycle.
 */
const StatementBuilder: React.FC = () => {
  const qc = useQueryClient();
  const { lang } = useDirection();
  const { data: options = [] } = useQuery({
    queryKey: ['parameters'],
    queryFn: () => apiService.getParameters(),
  });
  const [draft, setDraft] = React.useState<{ text: string; tone: StatementTone; sharedParameterId: string | null }>({
    text: '', tone: 'positive', sharedParameterId: null,
  });

  const { data: bank = [], isLoading, error: loadError } = useQuery({
    queryKey: ['statementBank'],
    queryFn: () => apiService.getStatementBank(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['statementBank'] });

  const createMutation = useMutation({
    mutationFn: () => apiService.createStatement({ ...draft, text: draft.text.trim() }),
    onSuccess: () => { invalidate(); setDraft(d => ({ ...d, text: '' })); },
  });
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<StatementTemplate> }) => apiService.updateStatement(id, data),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiService.deleteStatement(id),
    onSuccess: invalidate,
  });

  const error = createMutation.error ?? updateMutation.error ?? deleteMutation.error;

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ color: '#64748B', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.5 }}>
          {tr('nav.statements', lang)}
        </Typography>
        <Typography variant="h5">{tr('stmt.bankTitle', lang)}</Typography>
        <Typography sx={{ fontSize: '0.82rem', color: '#64748B', mt: 0.5 }}>{tr('stmt.bankHint', lang)}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(error)}</Alert>}

      {/* New statement */}
      <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, p: 2.5, mb: 2.5 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <TextField
            size="small"
            label={tr('stmt.text', lang)}
            value={draft.text}
            onChange={e => setDraft(d => ({ ...d, text: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter' && draft.text.trim()) createMutation.mutate(); }}
            inputProps={{ maxLength: 300, dir: 'auto' }}
            sx={{ flex: 1 }}
          />
          <ToneSelect label={tr('stmt.tone', lang)} value={draft.tone} onChange={tone => setDraft(d => ({ ...d, tone }))} />
          <ParameterSelect value={draft.sharedParameterId} options={options} onChange={sharedParameterId => setDraft(d => ({ ...d, sharedParameterId }))} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!draft.text.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {tr('stmt.add', lang)}
          </Button>
        </Stack>
      </Paper>

      {/* Bank */}
      {isLoading ? (
        <LoadingSpinner />
      ) : loadError ? (
        <ErrorAlert error={loadError} />
      ) : bank.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, border: '2px dashed #E2E8F0', borderRadius: 3, color: '#94A3B8' }}>
          <Typography>{tr('stmt.bankEmpty', lang)}</Typography>
        </Box>
      ) : (
        <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, px: 2.5, py: 1 }}>
          <Stack divider={<Box sx={{ borderTop: '1px solid #F1F5F9' }} />}>
            {bank.map(s => (
              <Stack key={s.id} direction="row" spacing={1.5} sx={{ alignItems: 'center', py: 1.25 }}>
                <ToneMark tone={s.tone} />
                <Typography sx={{ flex: 1, fontSize: '0.875rem' }} dir="auto">{s.text}</Typography>
                <ToneSelect value={s.tone} onChange={tone => updateMutation.mutate({ id: s.id, data: { tone } })} />
                <ParameterSelect
                  value={s.sharedParameterId}
                  options={options}
                  onChange={sharedParameterId => updateMutation.mutate({ id: s.id, data: { sharedParameterId } })}
                />
                <Tooltip title={tr('common.delete', lang)}>
                  <IconButton size="small" onClick={() => deleteMutation.mutate(s.id)} sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444' } }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default StatementBuilder;
